import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useIntakeOutcomeTrends } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";
import { format, parseISO } from "date-fns";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

const INTAKE_OUTCOME_SERIES: LegendSeries[] = [
  { key: "intakes", label: "Intakes", color: "hsl(var(--chart-3))" },
  { key: "outcomes", label: "Outcomes", color: "hsl(var(--chart-4))" },
  { key: "onHold", label: "On Hold", color: "hsl(var(--chart-5))" },
];

interface IntakeOutcomeChartProps {
  days: number;
}

function StatCard({ label, value, trend, testId }: { label: string; value: number; trend?: "up" | "down" | "neutral"; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-lg font-semibold">{value}</span>
        {trend === "up" && <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
        {trend === "down" && <TrendingDown className="h-3.5 w-3.5 text-destructive" />}
      </div>
    </div>
  );
}

export function IntakeOutcomeChart({ days }: IntakeOutcomeChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const { visibleSeries, toggleSeries } = useToggleableSeries(INTAKE_OUTCOME_SERIES);

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useIntakeOutcomeTrends(days, location, "weekly", apiSpecies);

  const chartData = useMemo(() => {
    if (!data?.series) return [];

    if (speciesFilter === "all") {
      return data.series.map((p) => ({
        date: format(parseISO(p.periodStart), "M/d/yy"),
        intakes: p.intakes,
        outcomes: p.outcomes,
        netChange: p.netChange,
        onHold: p.onHoldCount,
      }));
    }

    if (speciesFilter === "other") {
      return data.series.map((p) => {
        const sp = p.bySpecies?.OTHER;
        return {
          date: format(parseISO(p.periodStart), "M/d/yy"),
          intakes: sp?.intakes ?? 0,
          outcomes: sp?.outcomes ?? 0,
          netChange: sp?.netChange ?? 0,
          onHold: sp?.onHoldCount ?? 0,
        };
      });
    }

    return data.series.map((p) => ({
      date: format(parseISO(p.periodStart), "M/d/yy"),
      intakes: p.intakes,
      outcomes: p.outcomes,
      netChange: p.netChange,
      onHold: p.onHoldCount,
    }));
  }, [data, speciesFilter]);

  const tickInterval = useResponsiveTickInterval(chartData.length);

  const totals = useMemo(() => {
    if (!data?.totals) return null;

    if (speciesFilter === "other" && data.totals.bySpecies?.OTHER) {
      const sp = data.totals.bySpecies.OTHER;
      return { totalIntakes: sp.totalIntakes, totalOutcomes: sp.totalOutcomes, netChange: sp.netChange, currentOnHold: sp.currentOnHold };
    }

    return data.totals;
  }, [data, speciesFilter]);

  const netTrend = totals ? (totals.netChange > 0 ? "up" : totals.netChange < 0 ? "down" : "neutral") : "neutral";

  return (
    <Card data-testid="card-intake-outcome">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
            Intakes & Outcomes
          </CardTitle>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
            <SelectTrigger className="w-[140px]" data-testid="select-species-intake-outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Species</SelectItem>
              <SelectItem value="dogs">Dogs</SelectItem>
              <SelectItem value="cats">Cats</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4" data-testid="loading-intake-outcome">
            <div className="flex gap-3">
              <Skeleton className="h-16 flex-1" />
              <Skeleton className="h-16 flex-1" />
              <Skeleton className="h-16 flex-1" />
            </div>
            <Skeleton className="h-[300px] w-full" />
          </div>
        )}
        {isError && (
          <Alert variant="destructive" data-testid="error-intake-outcome">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load intake/outcome trends</span>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-intake-outcome">
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {data && data.series.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-intake-outcome">
            No intake or outcome data available yet.
          </div>
        )}
        {data && data.series.length > 0 && totals && (
          <>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-1" data-testid="text-intake-outcome-stats-hint">
                All records within selected date range
              </p>
              <div className="flex flex-wrap gap-3">
                <StatCard label="Total Intakes" value={totals.totalIntakes} testId="stat-total-intakes" />
                <StatCard label="Total Outcomes" value={totals.totalOutcomes} testId="stat-total-outcomes" />
                <StatCard label="Net Change" value={totals.netChange} trend={netTrend as "up" | "down" | "neutral"} testId="stat-net-change" />
                <StatCard label="Current On Hold" value={totals.currentOnHold} testId="stat-current-on-hold" />
              </div>
            </div>
            <InteractiveLegend
              series={INTAKE_OUTCOME_SERIES}
              visibleSeries={visibleSeries}
              onToggle={toggleSeries}
              testIdPrefix="intake-outcome-legend"
            />
            <div className="h-[300px]" data-testid="chart-intake-outcome">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={tickInterval} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      borderColor: "hsl(var(--border))",
                      borderRadius: "6px",
                      color: "hsl(var(--card-foreground))",
                    }}
                  />
                  {visibleSeries.has("intakes") && (
                    <Line
                      type="monotone"
                      dataKey="intakes"
                      stroke="hsl(var(--chart-3))"
                      strokeWidth={2}
                      dot={false}
                      name="Intakes"
                    />
                  )}
                  {visibleSeries.has("outcomes") && (
                    <Line
                      type="monotone"
                      dataKey="outcomes"
                      stroke="hsl(var(--chart-4))"
                      strokeWidth={2}
                      dot={false}
                      name="Outcomes"
                    />
                  )}
                  {visibleSeries.has("onHold") && (
                    <Line
                      type="monotone"
                      dataKey="onHold"
                      stroke="hsl(var(--chart-5))"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={false}
                      name="On Hold"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
